import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import Image from 'next/legacy/image'

import style from './imageGallery.module.scss';
import Icon from '@components/Icons';
import type { carouselImage } from '@lib/types';

const ImageGallery = ({ imagesArray }: {
    imagesArray: carouselImage[]
}) => {

    const lazyRoot = useRef(null);

    const modal = useRef(null);

    const [modalImage, setModalImage] = useState<number | null>(null);

    const getNewModalImageNum = useCallback((slideRight: boolean): number => {
        let newImage: number;

        if (slideRight) {
            if (modalImage! >= imagesArray.length - 1) {
                newImage = 0;
            } else {
                newImage = modalImage! + 1;
            }
        } else {
            if (modalImage! === 0) {
                newImage = imagesArray.length - 1;
            } else {
                newImage = modalImage! - 1;
            }
        }

        return newImage;
    }, [imagesArray, modalImage])

    useEffect(() => {
        if (modal.current !== null) modal.current.focus();
    }, [modalImage, modal])


    const handleArrow = useCallback((e) => {
        switch (e.keyCode) {
            case 39:
                setModalImage(getNewModalImageNum(true));
                break;
            case 37:
                setModalImage(getNewModalImageNum(false));
                break;
        }
    }, [getNewModalImageNum]);

    const displayModal = useCallback((arrayNum: number) => {
        return (
            <div className={style.imgModal}>
                <Image
                    loading="lazy"
                    src={imagesArray[arrayNum].path}
                    width={imagesArray[arrayNum].width}
                    height={imagesArray[arrayNum].height}
                    alt={imagesArray[arrayNum].alt}
                    placeholder='blur'
                    blurDataURL={imagesArray[arrayNum].blurDataURL}
                    lazyRoot={lazyRoot}
                />
                <div>
                    <div className={style.closeContainer}>
                        <button onClick={() => { setModalImage(null) }} aria-label="Close Booking" className={`${style.closeButton} ${style.close}`} >
                            <Icon iconClass={`nav-x`} />
                        </button>
                    </div>
                    <button
                        className={`${style.embla__button} ${style.embla__button_prev}`}
                        onClick={() => {
                            setModalImage(getNewModalImageNum(false));
                        }}
                    >
                        <Icon iconClass="nav-arrow-left" extraClass={style.embla__button__svg} />
                    </button>
                    <button
                        className={`${style.embla__button} ${style.embla__button_next}`}
                        onClick={() => {
                            setModalImage(getNewModalImageNum(true));
                        }}
                    >
                        <Icon iconClass="nav-arrow-right" extraClass={style.embla__button__svg} />
                    </button>
                </div>
            </div>)
    }, [imagesArray, getNewModalImageNum, setModalImage])


    const generateArray = useMemo(() => {
        const numLoops: number = Math.ceil(imagesArray.length / 5);
        const htmlArray: any = [];

        for (let i = 0; i < numLoops; i++) {
            const arrayOffset: number = i * 5; // offset for image array
            htmlArray.push(
                <div key={i}>
                    <div key={(arrayOffset + 0)} className={style.row}>
                        {imagesArray[(arrayOffset + 0)] &&
                            <div className={style.imgContainer} onClick={() => { setModalImage((arrayOffset + 0));  }} >
                                <Image
                                    loading="lazy"
                                    src={imagesArray[(arrayOffset + 0)].path}
                                    width={imagesArray[(arrayOffset + 0)].width}
                                    height={imagesArray[(arrayOffset + 0)].height}
                                    alt={imagesArray[(arrayOffset + 0)].alt}
                                    placeholder='blur'
                                    blurDataURL={imagesArray[(arrayOffset + 0)].blurDataURL}
                                    lazyRoot={lazyRoot}
                                />
                            </div>
                        }
                        {imagesArray[(arrayOffset + 1)] &&
                            <div className={style.imgContainer} onClick={() => { setModalImage((arrayOffset + 1)) }}>
                                <Image
                                    loading="lazy"
                                    src={imagesArray[(arrayOffset + 1)].path}
                                    width={imagesArray[(arrayOffset + 1)].width}
                                    height={imagesArray[(arrayOffset + 1)].height}
                                    alt={imagesArray[(arrayOffset + 1)].alt}
                                    placeholder='blur'
                                    blurDataURL={imagesArray[(arrayOffset + 1)].blurDataURL}
                                    lazyRoot={lazyRoot}
                                />
                            </div>
                        }
                    </div>
                    <div key={(arrayOffset + 1)} className={style.row}>
                        {imagesArray[(arrayOffset + 2)] &&
                            <div className={style.imgContainer} onClick={() => { setModalImage((arrayOffset + 2)) }}>
                                <Image
                                    loading="lazy"
                                    src={imagesArray[(arrayOffset + 2)].path}
                                    width={imagesArray[(arrayOffset + 2)].width}
                                    height={imagesArray[(arrayOffset + 2)].height}
                                    alt={imagesArray[(arrayOffset + 2)].alt}
                                    placeholder='blur'
                                    blurDataURL={imagesArray[(arrayOffset + 2)].blurDataURL}
                                    lazyRoot={lazyRoot}
                                />
                            </div>
                        }
                        {imagesArray[(arrayOffset + 3)] &&
                            <div className={style.imgContainer} onClick={() => { setModalImage((arrayOffset + 3)) }}>
                                <Image
                                    loading="lazy"
                                    src={imagesArray[(arrayOffset + 3)].path}
                                    width={imagesArray[(arrayOffset + 3)].width}
                                    height={imagesArray[(arrayOffset + 3)].height}
                                    alt={imagesArray[(arrayOffset + 3)].alt}
                                    placeholder='blur'
                                    blurDataURL={imagesArray[(arrayOffset + 3)].blurDataURL}
                                    lazyRoot={lazyRoot}
                                />
                            </div>
                        }
                        {imagesArray[(arrayOffset + 4)] &&
                            <div className={style.imgContainer} onClick={() => { setModalImage((arrayOffset + 4)) }}>
                                <Image
                                    loading="lazy"
                                    src={imagesArray[(arrayOffset + 4)].path}
                                    width={imagesArray[(arrayOffset + 4)].width}
                                    height={imagesArray[(arrayOffset + 4)].height}
                                    alt={imagesArray[(arrayOffset + 4)].alt}
                                    placeholder='blur'
                                    blurDataURL={imagesArray[(arrayOffset + 4)].blurDataURL}
                                    lazyRoot={lazyRoot}
                                />
                            </div>
                        }
                    </div>
                </div>
            );

        }

        return htmlArray;
    }, [imagesArray]);


    return (
        <>
            <div className={`${style.continer} ${style.gallerySmallTop}`}>
                {generateArray}
                {(modalImage !== null) &&
                    <div
                        ref={modal}
                        tabIndex={0}
                        onKeyDown={handleArrow}
                        className={`${style.imgModalBackground}`}
                        onClick={(e) => {
                            if (e.currentTarget != e.target) return;
                            setModalImage(null);
                        }}>
                        {displayModal(modalImage)}

                    </div>
                }

            </div>
        </>
    )
}

export default ImageGallery;


//setModalImage(null)